/*
 * V2 Predictive Search controller (no build step).
 *
 * Wires the existing v2 navigation search overlay to Shopify's AJAX Predictive
 * Search API using section rendering. As the user types we debounce, abort any
 * in-flight request, fetch the rendered `v2-predictive-search` section HTML, and
 * swap it into the results dropdown. Below the minimum query length we show the
 * server-rendered curated state (popular searches + featured products).
 *
 * Instantiated from the inline IIFE in snippets/v2-navigation-content.liquid:
 *   window.V2PredictiveSearch.init(root)
 */
(function () {
  'use strict';

  var MIN_QUERY = 2;
  var DEBOUNCE_MS = 250;
  var PRODUCT_FETCH_LIMIT = 10; // over-fetch so the wholesale filter can drop some
  var CACHE_MAX = 20;

  function Controller(root) {
    this.root = root;
    this.input = root.querySelector('[data-v2mn-search-input]');
    this.form = root.querySelector('.v2mn-search__form');
    this.results = root.querySelector('[data-v2mn-search-results]');
    if (!this.input || !this.results || !this.form) return;

    this.live = this.results.querySelector('[data-v2mn-search-live]');
    this.curated = this.results.querySelector('[data-v2mn-search-curated]');
    this.suggestUrl =
      this.results.getAttribute('data-v2mn-search-suggest-url') || '/search/suggest';
    this.sectionId =
      this.results.getAttribute('data-v2mn-search-section-id') || 'v2-predictive-search';
    this.minQuery =
      parseInt(this.results.getAttribute('data-v2mn-search-min'), 10) || MIN_QUERY;
    // Audience flag → chooses the product-query tag filter ('tag:wholesale' for
    // HD, '-tag:wholesale' for retail) so each audience gets precisely the right
    // products (the section still tag-filters as a safety net).
    this.isHD = this.results.getAttribute('data-v2mn-search-hd') === 'true';

    this.cache = new Map();
    this.abortController = null;
    this.debounceTimer = null;
    this.activeOption = null;

    this.onInput = this.onInput.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onResultsClick = this.onResultsClick.bind(this);
    this.onSubmit = this.onSubmit.bind(this);

    this.input.addEventListener('input', this.onInput);
    this.input.addEventListener('keydown', this.onKeydown);
    this.results.addEventListener('click', this.onResultsClick);
    this.form.addEventListener('submit', this.onSubmit);

    // ARIA combobox wiring.
    this.input.setAttribute('role', 'combobox');
    this.input.setAttribute('aria-autocomplete', 'list');
    this.input.setAttribute('aria-expanded', 'false');
    if (this.results.id) this.input.setAttribute('aria-controls', this.results.id);

    this.setState('idle');
  }

  Controller.prototype.setState = function (state) {
    this.results.setAttribute('data-state', state);
  };

  /* ---- Open / close hooks (called from the nav's openSearch/closeSearch) ---- */

  Controller.prototype.open = function () {
    this.results.setAttribute('aria-hidden', 'false');
    this.input.setAttribute('aria-expanded', 'true');
    // Below min length on open → show curated state.
    if (!this.input.value || this.input.value.trim().length < this.minQuery) {
      this.reset();
    }
  };

  Controller.prototype.close = function () {
    this.abort();
    window.clearTimeout(this.debounceTimer);
    this.clearActive();
    if (this.live) this.live.innerHTML = '';
    this.setState('idle');
    this.results.setAttribute('aria-hidden', 'true');
    this.input.setAttribute('aria-expanded', 'false');
    this.input.removeAttribute('aria-activedescendant');
  };

  Controller.prototype.hasCurated = function () {
    return !!(this.curated && this.curated.querySelector('[role="option"]'));
  };

  Controller.prototype.reset = function () {
    this.abort();
    this.clearActive();
    if (this.live) this.live.innerHTML = '';
    // Show the curated panel if it has content, otherwise keep the dropdown hidden.
    this.setState(this.hasCurated() ? 'curated' : 'idle');
  };

  Controller.prototype.abort = function () {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
  };

  /* ---- Input handling ---- */

  Controller.prototype.onInput = function () {
    var query = this.input.value.trim();
    window.clearTimeout(this.debounceTimer);

    if (query.length < this.minQuery) {
      this.reset();
      return;
    }

    var self = this;
    this.debounceTimer = window.setTimeout(function () {
      self.search(query);
    }, DEBOUNCE_MS);
  };

  Controller.prototype.buildUrl = function (query, types) {
    return (
      this.suggestUrl +
      '?q=' +
      encodeURIComponent(query) +
      '&section_id=' +
      encodeURIComponent(this.sectionId) +
      '&resources[type]=' +
      types +
      '&resources[limit]=' +
      PRODUCT_FETCH_LIMIT +
      '&resources[limit_scope]=each'
    );
  };

  Controller.prototype.search = function (query) {
    var key = query.toLowerCase();

    if (this.cache.has(key)) {
      this.render(this.cache.get(key));
      return;
    }

    this.abort();
    this.setState('loading');

    var controller = new AbortController();
    this.abortController = controller;
    var self = this;

    function toText(response) {
      if (!response.ok) throw new Error('Predictive search request failed');
      return response.text();
    }
    function done(html) {
      self.abortController = null;
      self.cacheSet(key, html);
      // Ignore stale responses if the query changed during the request.
      if (self.input.value.trim().toLowerCase() === key) {
        self.render(html);
      }
    }
    function fail(error) {
      if (error && error.name === 'AbortError') return;
      self.abortController = null;
      self.setState('idle');
    }

    // Two fetches, because Shopify returns zero query-suggestions for any
    // modified query: products from a tag-filtered query (precise per audience),
    // and suggestions + footer from the plain query.
    var tagFilter = this.isHD ? ' tag:wholesale' : ' -tag:wholesale';
    Promise.all([
      fetch(this.buildUrl(query + tagFilter, 'product'), { signal: controller.signal }).then(toText),
      fetch(this.buildUrl(query, 'product,query'), { signal: controller.signal }).then(toText)
    ])
      .then(function (parts) {
        done(self.assemble(parts[0], parts[1]));
      })
      .catch(fail);
  };

  // Combine the tag-filtered product fetch with the plain fetch's suggestions +
  // footer, so each audience gets precisely filtered products while the
  // suggestion text stays clean (a modified query returns no suggestions).
  Controller.prototype.assemble = function (productHtml, plainHtml) {
    function parse(html) {
      return new DOMParser().parseFromString(html, 'text/html');
    }
    var prodDoc = parse(productHtml);
    var plainDoc = parse(plainHtml);

    var prodGroup = prodDoc.querySelector('.v2mn-search-results__group--products');
    var sugGroup = plainDoc.querySelector('.v2mn-search-results__group--suggestions');
    var footer = plainDoc.querySelector('.v2mn-search-results__footer');
    var liveCount = plainDoc.querySelector('.v2mn-search-results__live-count');

    // Nothing to show → fall back to the plain no-results markup.
    if (!prodGroup && !sugGroup) return plainHtml;

    var html = '<div class="v2mn-search-results__inner" role="listbox" aria-label="Search results">';
    if (sugGroup) html += sugGroup.outerHTML;
    if (prodGroup) html += prodGroup.outerHTML;
    if (footer) html += footer.outerHTML;
    html += '</div>';
    if (liveCount) html += liveCount.outerHTML;
    return html;
  };

  Controller.prototype.cacheSet = function (key, html) {
    if (this.cache.size >= CACHE_MAX) {
      var oldest = this.cache.keys().next().value;
      this.cache.delete(oldest);
    }
    this.cache.set(key, html);
  };

  Controller.prototype.render = function (html) {
    if (!this.live) return;
    this.live.innerHTML = html;
    this.clearActive();
    var hasResults = !!this.live.querySelector('[role="option"]');
    var isEmpty = !!this.live.querySelector('.v2mn-search-results__empty');
    this.setState(isEmpty || !hasResults ? 'empty' : 'results');
  };

  /* ---- Options / keyboard navigation ---- */

  Controller.prototype.getOptions = function () {
    // Only options inside the currently visible region (curated vs live) are
    // navigable — the hidden region's options have no offsetParent.
    return Array.prototype.filter.call(
      this.results.querySelectorAll('[role="option"]'),
      function (el) {
        return el.offsetParent !== null;
      }
    );
  };

  Controller.prototype.clearActive = function () {
    if (this.activeOption) {
      this.activeOption.classList.remove('is-active');
      this.activeOption.setAttribute('aria-selected', 'false');
    }
    this.activeOption = null;
    this.input.removeAttribute('aria-activedescendant');
  };

  Controller.prototype.setActive = function (option) {
    this.clearActive();
    if (!option) return;
    option.classList.add('is-active');
    option.setAttribute('aria-selected', 'true');
    if (option.id) this.input.setAttribute('aria-activedescendant', option.id);
    this.activeOption = option;
    if (typeof option.scrollIntoView === 'function') {
      option.scrollIntoView({ block: 'nearest' });
    }
  };

  Controller.prototype.move = function (delta) {
    var options = this.getOptions();
    if (!options.length) return;
    var index = options.indexOf(this.activeOption);
    index += delta;
    if (index < 0) index = options.length - 1;
    if (index >= options.length) index = 0;
    this.setActive(options[index]);
  };

  Controller.prototype.onKeydown = function (event) {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.move(1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.move(-1);
        break;
      case 'Enter':
        if (this.activeOption) {
          event.preventDefault();
          this.activate(this.activeOption);
        }
        // Otherwise let the form submit to the full /search page.
        break;
      default:
        break;
    }
  };

  /* ---- Activation (mouse + keyboard) ---- */

  Controller.prototype.onResultsClick = function (event) {
    var term = event.target.closest('[data-v2mn-search-term]');
    if (term) {
      event.preventDefault();
      this.applyTerm(term.getAttribute('data-v2mn-search-term'));
    }
    // Anchor options navigate natively on click.
  };

  Controller.prototype.activate = function (option) {
    var term = option.getAttribute('data-v2mn-search-term');
    if (term) {
      this.applyTerm(term);
      return;
    }
    var link = option.matches('a[href]') ? option : option.querySelector('a[href]');
    if (link) window.location.href = link.href;
  };

  Controller.prototype.applyTerm = function (term) {
    if (!term) return;
    this.input.value = term;
    this.input.focus();
    window.clearTimeout(this.debounceTimer);
    this.search(term.trim());
  };

  Controller.prototype.onSubmit = function (event) {
    window.clearTimeout(this.debounceTimer);
    var term = this.input.value.trim();
    if (!term) return; // let the browser handle an empty submit
    // Navigate to the results page with the audience tag filter appended to the
    // query (retail excludes wholesale, HD includes only wholesale) so it
    // returns full pages. We build the URL and navigate ourselves rather than
    // editing the input value, so the operator never flashes in the search box.
    if (event && typeof event.preventDefault === 'function') event.preventDefault();
    var tagFilter = term.indexOf('tag:wholesale') !== -1 ? '' : (this.isHD ? ' tag:wholesale' : ' -tag:wholesale');
    var action = this.form.getAttribute('action') || '/search';
    window.location.assign(
      action + '?q=' + encodeURIComponent(term + tagFilter) + '&type=product&options%5Bprefix%5D=last'
    );
  };

  /* ---- Public API ---- */

  window.V2PredictiveSearch = {
    instances: [],
    init: function (root) {
      if (!root || root.__v2psInit) return null;
      var controller = new Controller(root);
      root.__v2psInit = true;
      root.__v2ps = controller;
      this.instances.push(controller);
      return controller;
    }
  };
})();
